import { useId } from 'react';
import { FeatureUnavailableNotice } from '../../../shared/components/FeatureUnavailableNotice';
import { formatDate, formatMoney, formatPeriod, plural } from '../../check-batch/format';
import { checkNumberRange } from '../helpers';
import type { ContractorBatch } from '../types';
import { ContractorCheckNote } from './ContractorNotes';

interface ReviewProps {
  batch: ContractorBatch;
  confirmed: boolean;
  onConfirmedChange: (value: boolean) => void;
  blockedReason: string | null;
  /** False in a production bundle: the approve action is not offered at all (EPN-98). */
  approveAvailable: boolean;
  busy: boolean;
  onBack: () => void;
  onDiscard: () => void;
  onSaveDraft: () => void;
  onApprove: () => void;
  onRecheck: () => void;
}

/** Step 3. Totals are the server's; each payment is listed so it can be checked, not just the batch total. */
export function ContractorReview(props: ReviewProps) {
  const { batch, confirmed, blockedReason, approveAvailable, busy } = props;
  const checkId = useId();
  const included = batch.lines.filter((l) => l.included);
  const canApprove = confirmed && blockedReason === null && approveAvailable && !busy;

  return (
    <section aria-labelledby="wc-review-title">
      <ContractorCheckNote />
      <h3 id="wc-review-title">Review Payment</h3>
      <dl className="wc-cards">
        <div><dt>Pay Date</dt><dd>{formatDate(batch.payDate)}</dd></div>
        <div><dt>Payment Period</dt><dd>{batch.periodStartDate && batch.periodEndDate ? formatPeriod(batch.periodStartDate, batch.periodEndDate) : 'Not set'}</dd></div>
        <div><dt>Payment Method</dt><dd>Write Check</dd></div>
        <div><dt>Contractors Selected</dt><dd data-testid="review-contractors">{plural(batch.totals.contractorsIncluded, 'Contractor', 'Contractors')}</dd></div>
        <div><dt>Payment Total</dt><dd data-testid="review-payment">{formatMoney(batch.totals.paymentTotal)}</dd></div>
        <div><dt>Check Total</dt><dd className="wc-accent" data-testid="review-check">{formatMoney(batch.totals.checkTotal)}</dd></div>
        <div className="wc-cards__wide"><dt>Check {included.length === 1 ? 'Number' : 'Numbers'}</dt><dd data-testid="review-numbers">{checkNumberRange(included.map((l) => l.checkNumber))}</dd></div>
      </dl>

      <h4 className="wc-subhead">Payments in this batch</h4>
      <div className="table-scroll" role="region" aria-label="Payments in this batch" tabIndex={0}>
        <table className="wc-table wc-table--compact" aria-label="Payments in this batch">
          <thead>
            <tr><th scope="col">Contractor</th><th scope="col">Description</th><th scope="col">Check Number</th><th scope="col" className="wc-num">Check Amount</th></tr>
          </thead>
          <tbody>
            {included.map((l) => (
              <tr key={l.id}><td>{l.contractorName}</td><td className="wc-wrap">{l.description}</td><td>{l.checkNumber}</td><td className="wc-num">{formatMoney(l.amount)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="wc-confirm">
        <p className="wc-confirm__label">Before you approve:</p>
        <label className="wc-check" htmlFor={checkId}>
          <input id={checkId} type="checkbox" checked={confirmed} disabled={busy} onChange={(e) => props.onConfirmedChange(e.target.checked)} />
          <span>I confirm that I have reviewed all payment details, and that the amounts, recipients, and pay dates are correct to the best of my knowledge.</span>
        </label>
      </div>

      {blockedReason && (
        <p className="wc-warn" role="status" data-testid="approve-blocked">
          {blockedReason}{' '}
          <button type="button" className="wc-link" onClick={props.onRecheck}>Go to the readiness check</button>
        </p>
      )}
      {!approveAvailable && (
        <FeatureUnavailableNotice feature="Approving a contractor payment batch">
          Approval is not available in this release. Nothing has been approved, printed, mailed or paid.
        </FeatureUnavailableNotice>
      )}

      <div className="wc-footer wc-footer--review">
        <button type="button" className="wc-btn wc-btn--outline" onClick={props.onBack} disabled={busy}>Back to Readiness Check</button>
        <div className="wc-footer__actions">
          <button type="button" className="wc-link wc-link--muted" onClick={props.onDiscard} disabled={busy}>Discard Draft</button>
          <button type="button" className="wc-btn wc-btn--green" onClick={props.onSaveDraft} disabled={busy}>Save as Draft</button>
          {approveAvailable && (
            <button type="button" className="wc-btn wc-btn--primary" onClick={props.onApprove} disabled={!canApprove}>Approve Payment</button>
          )}
        </div>
      </div>
    </section>
  );
}
